import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, 
        Tooltip as ChartTooltip, Legend, Filler } from 'chart.js';

import { Box, Paper, Typography, CircularProgress, IconButton, Stack, TextField, Tooltip,
        Dialog, DialogTitle, DialogContent, DialogActions, Button, 
        Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel 
} from '@mui/material';
import { ChevronLeft, ChevronRight, TableChartOutlined, SsidChart, BarChart, FiberManualRecord, Refresh } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import * as XLSX from "xlsx";
import moment from 'moment';

import { generateExcelFileName } from '../include'

ChartJS.register( CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, ChartTooltip, Legend, Filler );

const whiteBackgroundPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};

export const AccessHist = (props) => {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [minDate, setMinDate] = useState(moment("2022-08-01"));
  const [chartType, setChartType] = useState('bar'); 

  // Modal 1: Lista de Usuários do dia
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDayDetail, setSelectedDayDetail] = useState(null);
  const [detailData, setDetailData] = useState([]); 
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Modal 2: Analítico por Usuário
  const [isAnalyticModalOpen, setIsAnalyticModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [analyticData, setAnalyticData] = useState([]);
  const [isLoadingAnalytic, setIsLoadingAnalytic] = useState(false);
  
  // Ordenação da tabela analítica
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');

  // Refs
  const isFirstRender = useRef(true);
  const prevMonthRef = useRef(moment().format('YYYY-MM'));

  const loadAccessHistory = useCallback(async () => {
    if (!props.api) return;
    
    setIsLoading(true);
    setHasError(false);
    const isCurrentMonth = selectedDate.isSame(moment(), 'month');
    const endDateValue = isCurrentMonth ? moment().endOf('day') : selectedDate.clone().endOf('month');
    const params = {
      startDate: selectedDate.clone().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      endDate: endDateValue.format('YYYY-MM-DD HH:mm:ss'),
    };
    
    try {
      const res = await props.api.post('/logs/getHistory', params);
      const dataToSet = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      setHistoryData(dataToSet);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.api, selectedDate]);

  const fetchMinDate = useCallback(async () => {
    if (!props.api) return;
    try {
      const res = await props.api.get('/logs/getOldestDate');
      const data = res.data || res;
      if (data && data.min_date) setMinDate(moment(data.min_date));
    } catch (error) {
      console.error("Erro ao obter data limite:", error);
    }
  }, [props.api]);

  useEffect(() => {
    const currentMonthStr = selectedDate.format('YYYY-MM');

    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchMinDate();
      loadAccessHistory();
    } else if (prevMonthRef.current !== currentMonthStr) {
      loadAccessHistory();
    }
    prevMonthRef.current = currentMonthStr;
  }, [selectedDate, loadAccessHistory, fetchMinDate]);

  const handlePrevMonth = (e) => { e.stopPropagation(); setSelectedDate(prev => prev.clone().subtract(1, 'month')); };
  const handleNextMonth = (e) => { e.stopPropagation(); setSelectedDate(prev => prev.clone().add(1, 'month')); };

  // Clique no Gráfico - Abre Modal 1
  const handleChartClick = useCallback(async (event, elements, chart) => {
    if (!elements || elements.length === 0) return;

    const dataIndex = elements[0].index;
    const dayClicked = dataIndex + 1; 
    
    const clickedDateStr = selectedDate.clone().date(dayClicked).format('YYYY-MM-DD');
    
    setSelectedDayDetail(clickedDateStr);
    setIsModalOpen(true);
    setIsLoadingDetails(true);

    try {
      const res = await props.api.post('/logs/getUsersByDate', { date: clickedDateStr });
      const dataToSet = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      setDetailData(dataToSet);
    } catch (error) {
      console.error("Erro ao carregar detalhes do dia:", error);
      setDetailData([]);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [selectedDate, props.api]);

  // Clique na Linha do Usuário - Abre Modal 2
  const handleUserRowClick = useCallback(async (user) => {
    setSelectedUser(user.nome_usuario);
    setIsAnalyticModalOpen(true);
    setIsLoadingAnalytic(true);
    try {
      const queryParams = new URLSearchParams({
        dataini: selectedDayDetail,
        datafim: selectedDayDetail, 
        username: user.nome_usuario,
        logar: false
      }).toString();
      const res = await props.api.get(`/logs?${queryParams}`);
      
      // O backend retorna um objeto { logs: [...] }
      const dataToSet = res?.data?.logs || res?.logs || [];
      setAnalyticData(dataToSet);
    } catch (error) {
      console.error("Erro ao carregar o analítico do usuário:", error);
      setAnalyticData([]);
    } finally {
      setIsLoadingAnalytic(false);
    }
  }, [selectedDayDetail, props.api]);

  // Lógica de Ordenação do Analítico
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedAnalyticData = useMemo(() => {
    return [...analyticData].sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      
      if (valB < valA) { return order === 'asc' ? 1 : -1; }
      if (valB > valA) { return order === 'asc' ? -1 : 1; }
      return 0;
    });
  }, [analyticData, order, orderBy]);


  const chartOptions = useMemo(() => ({
    onClick: handleChartClick,  
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { 
        mode: 'index', 
        intersect: false,
        filter: (tooltipItem) => {
          const isCurrentMonth = selectedDate.isSame(moment(), 'month');
          const today = moment().date();
          const itemDay = tooltipItem.dataIndex + 1;
          return !(isCurrentMonth && itemDay > today);
        },
        callbacks: {
          label: (context) => {
            const isTrigger = context.dataset.label === 'TRIGGER';
            const val = context.raw;
            if (!isTrigger) {
              return `${context.dataset.label}: ${val === null || val === undefined ? 'N/D' : val}`;
            }
            return null; 
          }
        }
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { 
        grid: { display: false },
        ticks: {
          color: (context) => {
            const isCurrentMonth = selectedDate.isSame(moment(), 'month');
            const today = moment().date();
            const tickDay = context.index + 1;

            if (isCurrentMonth && tickDay > today) return '#d1d1d1'; 
            
            const tickDate = selectedDate.clone().date(tickDay);
            const dayOfWeek = tickDate.day(); 

            if (dayOfWeek === 0 || dayOfWeek === 6) return '#d32f2f'; 
            return '#666'; 
          }
        }
      }
    }
  }), [selectedDate, handleChartClick]);

  const chartData = useMemo(() => {
    const daysInMonth = selectedDate.daysInMonth();
    const isCurrentMonth = selectedDate.isSame(moment(), 'month');
    const today = moment().startOf('day');

    const labels = [];
    const dataValues = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const currentLoopDate = selectedDate.clone().date(day).startOf('day');
      labels.push(currentLoopDate.format('DD/MM'));

      if (isCurrentMonth && currentLoopDate.isAfter(today)) {
        dataValues.push(null);
      } else {
        const dateStr = currentLoopDate.format('YYYY-MM-DD');
        const found = historyData.find(d => {
          const rowDate = d.date || d.created_at;
          return rowDate && moment(rowDate).format('YYYY-MM-DD') === dateStr;
        });
        
        dataValues.push(found ? Number(found.count || 0) : 0);
      }
    }

    const dummyData = labels.map((_, i) => {
      const date = selectedDate.clone().date(i + 1).startOf('day');
      return (isCurrentMonth && date.isAfter(today)) ? null : 0;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Acessos Únicos por Usuário',
          data: dataValues,
          borderColor: '#2196f3',
          backgroundColor: chartType === 'line' ? 'rgba(33, 150, 243, 0.1)' : '#2196f3',
          stack: 'combined',
          fill: chartType === 'line',
          pointRadius: dataValues.map(v => v === null ? 0 : 4),
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'TRIGGER',
          data: dummyData,
          stack: 'combined',  
          borderColor: 'transparent',   
          backgroundColor: 'transparent',
          pointRadius: 0,
          fill: false
        }
      ]
    };
  }, [historyData, selectedDate, chartType]);

  const exportToExcel = () => {
    setIsExporting(true);
    try {
      const isCurrentMonth = selectedDate.isSame(moment(), 'month');
      const lastDayToExport = isCurrentMonth ? moment().date() : selectedDate.daysInMonth();
              
      const rows = [];
      for (let day = 1; day <= lastDayToExport; day++) {
        const currentDate = selectedDate.clone().date(day);
        const dateStr = currentDate.format('YYYY-MM-DD');
        
        const found = historyData.find(d => {
          const rowDate = d.date || d.created_at;
          return rowDate && moment(rowDate).format('YYYY-MM-DD') === dateStr;
        });
        const accessCount = found ? Number(found.count || 0) : 0;
        
        rows.push([currentDate.format('DD/MM/YYYY'), accessCount]);
      }

      const sheetData = [
        ["Histórico de Acessos"], 
        [`Competência: ${selectedDate.format('MMMM/YYYY')}`], 
        [], 
        ["Data", "Acessos Únicos por Usuário"], 
        ...rows
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      worksheet['!cols'] = [{ wch: 15 }, { wch: 18 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Acessos");
      XLSX.writeFile(workbook, generateExcelFileName('Acessos'));
    } catch (error) {
      console.error("Erro Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // RENDER
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)', width: '100%', overflow: 'hidden', backgroundColor: '#fff' }}>
        
        {/* Topbar Omitida para brevidade (Manter exatamente o seu código aqui da Stack de DatePicker etc) */}
        <div style={{ flex: '0 0 auto', padding: '15px 15px 10px 25px', backgroundColor: '#fff', zIndex: 10 }}>
          <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
            <DatePicker
              label="Competência (Mês/Ano)"
              views={['year', 'month']}
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              minDate={minDate}
              maxDate={moment()}
              renderInput={(params) => (
                <TextField {...params} size="small" sx={{ width: 300 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <Tooltip title="Mês anterior" arrow>
                          <span>
                            <IconButton size="small" onClick={handlePrevMonth} disabled={selectedDate.isSameOrBefore(minDate, 'month')}>
                              <ChevronLeft fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Mês corrente" arrow>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedDate(moment()); 
                              }} 
                              disabled={selectedDate.isSame(moment(), 'month')}
                            >
                              <FiberManualRecord fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Mês seguinte" arrow>
                          <span>
                            <IconButton size="small" onClick={handleNextMonth} disabled={selectedDate.isSameOrAfter(moment(), 'month')}>
                              <ChevronRight fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Tooltip title="Atualizar" arrow>
              <span>
                <IconButton onClick={loadAccessHistory} disabled={isLoading} color="default">
                  <Refresh sx={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </span>
            </Tooltip>

            <Box sx={{ width: 6 }} />

            <Tooltip title="Gráfico de Linha" arrow>
              <IconButton onClick={() => setChartType('line')} sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', color: chartType === 'line' ? '#1976d2' : '#000' }}>
                <SsidChart />
              </IconButton>
            </Tooltip>

            <Tooltip title="Gráfico de Barras" arrow>
              <IconButton onClick={() => setChartType('bar')} sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', color: chartType === 'bar' ? '#1976d2' : '#000' }}>
                <BarChart />
              </IconButton>
            </Tooltip>

            <Tooltip title="Exportar para Excel" arrow>
              <span>
                <IconButton sx={{ ml: 2, color: '#106436' }} onClick={exportToExcel} disabled={isLoading || isExporting}>
                  {isExporting ? <CircularProgress size={24} color="inherit" /> : <TableChartOutlined />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </div>

        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '10px 25px 25px 25px' }}>
          {hasError ? (
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff5f5' }}>
              <Typography color="error">Erro ao carregar dados.</Typography>
            </Paper>
          ) : isLoading ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper elevation={2} sx={{ p: 2, height: '450px', borderRadius: '8px', borderTop: `6px solid #2196f3`, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1 }}>Acessos Únicos por Usuário</Typography>
              <Box sx={{ flexGrow: 1, position: 'relative' }}>
                {chartType === 'line' ? (
                  <Line data={chartData} options={chartOptions} plugins={[whiteBackgroundPlugin]} />
                ) : (
                  <Bar data={chartData} options={chartOptions} plugins={[whiteBackgroundPlugin]} />
                )}
              </Box>
            </Paper>
          )}
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>

{/* Modal 1: Detalhes do Dia */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Acessos por usuário no dia {selectedDayDetail ? moment(selectedDayDetail).format('DD/MM/YYYY') : ''}
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailData.length > 0 ? (            
            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      Usuário
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="center">
                      Acessos
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailData.map((registro, index) => (
                    /* Envolvendo a linha com o Tooltip e movendo a key para cá */
                    <Tooltip 
                      key={index} 
                      title="Relação de acessos" 
                      arrow 
                      placement="top"
                    >
                      <TableRow 
                        hover
                        onClick={() => handleUserRowClick(registro)}
                        sx={{ 
                          cursor: 'pointer', 
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {registro.nome_usuario || "Usuário Desconhecido"}
                        </TableCell>
                        <TableCell align="center">
                          {registro.total_acessos}
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center" py={4}>
              Nenhum acesso encontrado nesta data.
            </Typography>
          )}          
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal 2: Analítico do Usuário */}
      <Dialog 
        open={isAnalyticModalOpen} 
        onClose={() => setIsAnalyticModalOpen(false)} 
        maxWidth="md" 
        fullWidth
        // Adicionando um zIndex superior para garantir que fique sempre à frente do primeiro modal
        style={{ zIndex: 1301 }} 
      >
        <DialogTitle>
          Acessos de {selectedUser} em {selectedDayDetail ? moment(selectedDayDetail).format('DD/MM/YYYY') : ''}
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingAnalytic ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : analyticData.length > 0 ? (
            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: '4px', mt: 1, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '200px' }}>
                      <TableSortLabel
                        active={orderBy === 'created_at'}
                        direction={orderBy === 'created_at' ? order : 'asc'}
                        onClick={() => handleRequestSort('created_at')}
                      >
                        Horário
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'url'}
                        direction={orderBy === 'url' ? order : 'asc'}
                        onClick={() => handleRequestSort('url')}
                      >
                        Caminho
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAnalyticData.map((row, i) => (
                    <TableRow key={i} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        {row.created_at ? moment(row.created_at).format('DD/MM/YYYY HH:mm:ss') : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>
                        {row.url}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center" py={4}>
              Nenhum acesso encontrado para este usuário.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAnalyticModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

    </LocalizationProvider>
  );
};